import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import Drawer from "@/components/ui/drawer";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import Dropdown from "@/components/ui/dropdown";
import Textarea from "@/components/ui/textarea";
import LoadingScreen from "@/components/ui/loading";
import {
  REQUEST_STATUS_CHIP,
  STEP_STATUS_CHIP,
} from "../constants/approval-status.constants";
import { approvalRequestsApi } from "../services/approval-flows.api";
import {
  ApprovalAssigneeType,
  ApprovalParallelPolicy,
  ApprovalRequestStatus,
  ApprovalStepStatus,
  groupApprovalSteps,
  type ApprovalRequest,
  type ApprovalRequestStep,
} from "../types/approval-flows.types";
import styles from "./ApprovalRequestDrawer.module.scss";

interface ApprovalRequestDrawerProps {
  requestId: string | null;
  onClose: () => void;
  /** Karar/iptal sonrası listeyi tazelemek için. */
  onChanged: () => void;
}

/** Zaman çizelgesindeki noktanın rengini adım durumundan türetir. */
const dotClassOf = (status: ApprovalStepStatus, isCurrent: boolean): string => {
  if (status === ApprovalStepStatus.APPROVED) return styles.dotApproved;
  if (status === ApprovalStepStatus.REJECTED) return styles.dotRejected;
  if (status === ApprovalStepStatus.SKIPPED) return styles.dotSkipped;
  return isCurrent ? styles.dotCurrent : styles.dotPending;
};

/**
 * Kademenin bütünsel durumu — noktanın rengi bundan gelir.
 *
 * Paralel kademede dallar farklı durumlarda olabilir: bir dal onaylanmışken
 * kardeşi hâlâ bekliyor olabilir (ALL politikası) ya da atlanmış olabilir (ANY).
 * Kademe, bir dalı bile reddedildiyse reddedilmiş; hâlâ bekleyen dalı varsa
 * bekliyor; kalanı onay/atlama karışımıysa geçilmiş sayılır.
 */
const groupStatusOf = (branches: ApprovalRequestStep[]): ApprovalStepStatus => {
  if (branches.some((b) => b.status === ApprovalStepStatus.REJECTED)) return ApprovalStepStatus.REJECTED;
  if (branches.some((b) => b.status === ApprovalStepStatus.PENDING)) return ApprovalStepStatus.PENDING;
  if (branches.some((b) => b.status === ApprovalStepStatus.APPROVED)) return ApprovalStepStatus.APPROVED;
  return ApprovalStepStatus.SKIPPED;
};

/** Dalın onaycısını okunabilir tek satıra çevirir. */
const assigneeLabelOf = (step: ApprovalRequestStep): string => {
  if (step.assigneeType !== ApprovalAssigneeType.ROLE) return `Kişi: ${step.user?.name ?? "-"}`;
  // Dal birden fazla role atanmış olabilir (ör. Daire Başkanı / İşletme
  // Müdürü); hepsi listelenir.
  const names = step.roles?.length ? step.roles.map((r) => r.name).join(", ") : (step.role?.name ?? "-");
  return `Rol: ${names}`;
};

/** Talep detayı: adım zaman çizelgesi ve sırası gelen adım için karar aksiyonu. */
const ApprovalRequestDrawer: React.FC<ApprovalRequestDrawerProps> = ({
  requestId,
  onClose,
  onChanged,
}) => {
  const [request, setRequest] = useState<ApprovalRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /**
   * Paralel kademede kararın yazılacağı dal. Boş bırakılırsa backend
   * kullanıcının düştüğü ilk bekleyen dalı seçer — kullanıcı yalnızca tek dala
   * düşüyorsa (olağan durum) seçim hiç sorulmaz.
   */
  const [branchId, setBranchId] = useState<string | null>(null);

  useEffect(() => {
    if (!requestId) {
      setRequest(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setComment("");
    setBranchId(null);
    approvalRequestsApi
      .getById(requestId)
      .then((data) => {
        if (!cancelled) setRequest(data);
      })
      .catch(() => {
        if (!cancelled) onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestId, onClose]);

  const handleDecision = async (approved: boolean) => {
    if (!requestId) return;
    setSubmitting(true);
    try {
      const updated = await approvalRequestsApi.decide(requestId, {
        approved,
        comment: comment.trim() || undefined,
        stepId: branchId ?? undefined,
      });
      setRequest(updated);
      setComment("");
      setBranchId(null);
      toast.success(approved ? "Adım onaylandı" : "Talep reddedildi");
      onChanged();
    } catch {
      // 403 (sıra sizde değil) / 400 (talep sonuçlanmış) global toast ile gösterilir.
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!requestId) return;
    setSubmitting(true);
    try {
      const updated = await approvalRequestsApi.cancel(requestId);
      setRequest(updated);
      toast.success("Talep iptal edildi");
      onChanged();
    } catch {
      // Yetkisiz iptal / sonuçlanmış talep hataları global toast ile gösterilir.
    } finally {
      setSubmitting(false);
    }
  };

  const isPending = request?.status === ApprovalRequestStatus.PENDING;

  /** Adımlar kademe kademe çizilir; paralel dallar tek kademenin altında toplanır. */
  const stepGroups = useMemo(() => groupApprovalSteps(request?.steps ?? []), [request]);

  /**
   * Sırası gelen kademenin hâlâ karar bekleyen dalları.
   *
   * Birden fazlaysa kullanıcıya hangi dal adına karar verdiği sorulur: aynı
   * kişi iki dalın da onaycısı olabilir (ör. hem Daire Başkanı hem İşletme
   * Müdürü rolündeyse) ve ALL politikasında bu iki ayrı onay demektir.
   */
  const currentBranches = useMemo(
    () =>
      (request?.steps ?? []).filter((s) => s.isCurrent && s.status === ApprovalStepStatus.PENDING),
    [request],
  );
  const branchOptions = currentBranches.map((step) => ({
    value: step.id,
    label: `${step.title} — ${assigneeLabelOf(step)}`,
  }));

  return (
    <Drawer
      isOpen={Boolean(requestId)}
      onClose={onClose}
      title={request?.title ?? "Onay Talebi"}
      titleBadge={
        request ? (
          <Chip size="small" type={REQUEST_STATUS_CHIP[request.status].type} variant="soft">
            {REQUEST_STATUS_CHIP[request.status].label}
          </Chip>
        ) : undefined
      }
      width="640px"
    >
      {loading || !request ? (
        <LoadingScreen />
      ) : (
        <div className={styles.body}>
          <dl className={styles.meta}>
            <div>
              <dt>Akış</dt>
              <dd>{request.flowTitle}</dd>
            </div>
            <div>
              <dt>Talep Eden</dt>
              <dd>{request.requesterName}</dd>
            </div>
            <div>
              <dt>İlerleme</dt>
              <dd>
                {request.decidedSteps} / {request.totalSteps} adım
              </dd>
            </div>
            {request.description && (
              <div className={styles.metaWide}>
                <dt>Açıklama</dt>
                <dd>{request.description}</dd>
              </div>
            )}
          </dl>

          <div className={styles.timeline}>
            <span className={styles.sectionLabel}>ADIMLAR</span>
            <ol className={styles.stepList}>
              {stepGroups.map(({ stepOrder, branches }) => {
                const isParallel = branches.length > 1;
                const groupStatus = groupStatusOf(branches);
                const isCurrentGroup = branches.some((b) => b.isCurrent);

                return (
                  <li className={styles.step} key={stepOrder}>
                    <span className={`${styles.dot} ${dotClassOf(groupStatus, isCurrentGroup)}`} />
                    <div className={styles.stepContent}>
                      <div className={styles.stepHead}>
                        <span className={styles.stepTitle}>
                          {stepOrder}.{" "}
                          {isParallel
                            ? branches.map((b) => b.title).join(" / ")
                            : branches[0].title}
                        </span>
                        <Chip size="small" type={STEP_STATUS_CHIP[groupStatus].type} variant="soft">
                          {STEP_STATUS_CHIP[groupStatus].label}
                        </Chip>
                      </div>

                      {isParallel && (
                        <div className={styles.parallelNote}>
                          Paralel adım —{" "}
                          {branches[0].parallelPolicy === ApprovalParallelPolicy.ALL
                            ? "tüm dallar onaylamalı"
                            : "dallardan biri yeterli"}
                        </div>
                      )}

                      {/* Tek dallı kademede dal kutusu çizilmez: kademe zaten
                          tek onaycıdır, çerçeve gereksiz gürültü olurdu. */}
                      <div className={isParallel ? styles.branchList : undefined}>
                        {branches.map((step) => (
                          <div
                            className={isParallel ? styles.branchItem : undefined}
                            key={step.id}
                          >
                            {isParallel && (
                              <div className={styles.branchHead}>
                                <span className={styles.branchTitle}>{step.title}</span>
                                <Chip
                                  size="small"
                                  type={STEP_STATUS_CHIP[step.status].type}
                                  variant="soft"
                                >
                                  {STEP_STATUS_CHIP[step.status].label}
                                </Chip>
                              </div>
                            )}
                            <div className={styles.assignee}>{assigneeLabelOf(step)}</div>
                            {step.description && (
                              <div className={styles.stepDesc}>{step.description}</div>
                            )}
                            {step.decidedByName && (
                              <div className={styles.decision}>
                                {step.decidedByName}
                                {step.decidedAt &&
                                  ` — ${new Date(step.decidedAt).toLocaleString("tr-TR")}`}
                                {step.comment && (
                                  <div className={styles.comment}>“{step.comment}”</div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {isPending && request.canDecide && (
            <div className={styles.actionBox}>
              <span className={styles.actionLabel}>
                Sıra sizde — “{request.currentStepTitle}” adımı
              </span>
              {/* Seçim yalnızca sırası gelen kademede birden fazla dal
                  beklerken sorulur; kullanıcı tek dala düşüyorsa backend onu
                  kendisi seçer ve soru hiç görünmez. */}
              {branchOptions.length > 1 && (
                <Dropdown
                  label="Hangi dal adına karar veriyorsunuz?"
                  onSelect={(value) => setBranchId((value as string) || null)}
                  options={branchOptions}
                  placeholder="Otomatik — sıradaki dalım"
                  selectedLabel={branchOptions.find((o) => o.value === branchId)?.label}
                  value={branchId}
                />
              )}
              <Textarea
                label="Karar Notu"
                maxLength={2000}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Kararınızla ilgili not ekleyin (opsiyonel)"
                rows={2}
                value={comment}
              />
              <div className={styles.actionButtons}>
                <Button
                  color="error"
                  loading={submitting}
                  onClick={() => handleDecision(false)}
                  variant="outlined"
                >
                  Reddet
                </Button>
                <Button
                  color="success"
                  loading={submitting}
                  onClick={() => handleDecision(true)}
                  variant="contained"
                >
                  Onayla
                </Button>
              </div>
            </div>
          )}

          {isPending && !request.canDecide && (
            <div className={styles.waitingBox}>
              Sıra “{request.currentStepTitle}” adımının onaycısında. Bu adımda karar verme
              yetkiniz yok.
            </div>
          )}

          {isPending && (
            <div className={styles.cancelRow}>
              <Button
                color="error"
                loading={submitting}
                onClick={handleCancel}
                variant="text"
              >
                Talebi İptal Et
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ApprovalRequestDrawer;
